import { AirActionTrackingLocation, useTrackSetCustomPdfThumbnail } from '@air/analytics';
import { Assets } from '@air/api';
import { Clip, ClipStatus } from '@air/api/types';
import { DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useToasts } from '@air/provider-toast';
import { convertUnknownToError } from '@air/utils-error';
import { useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUnmount } from 'react-use';

import { getVersionsListKey } from '~/constants/react-query-keys';
import { QueryParamNames } from '~/constants/search';
import { getClipStatusUpdaterKey } from '~/hooks/useClipStatusChecker';
import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useURLClipIdSelector } from '~/hooks/useURLClipIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  centralizedClipAssetIdSelector,
  centralizedClipIdSelector,
  centralizedClipPDFAssetSelector,
  centralizedClipStatusSelector,
} from '~/store/centralizedClip/selectors';
import { useUpdateAssetInAllViews } from '~/utils/mutateUtils/GalleryAssets';

export const usePDFAssetLinkWithData = ({ getFallbackUrl }: { getFallbackUrl: () => Promise<string> }) => {
  // This is required because PDF links have an expiry date attached to them that changes
  // every time the pdf is fetched from the API. This includes updating the asset.
  // Without this, updating any part of the asset, and having the centralizedClip
  // state updated as a result will produce a new pdf link, and cause the viewer to do a full reload.
  const [data, setData] = useState<ArrayBuffer | null>(null);
  const [hasError, setHasError] = useState(false);
  const pdfAsset = useSelector(centralizedClipPDFAssetSelector);
  const downloadingAsset = useRef<undefined | string>(undefined);
  const clipId = useSelector(centralizedClipIdSelector);
  const previousClipId = useRef<string | undefined>(undefined);

  const abortController = useRef<AbortController | null>(null);

  const getFallbackUrlRef = useRef(getFallbackUrl);
  getFallbackUrlRef.current = getFallbackUrl;

  const fetchPdfAsset = useCallback(async () => {
    if (downloadingAsset.current === pdfAsset || clipId === previousClipId.current) {
      return;
    }

    setData(null);
    setHasError(false);

    downloadingAsset.current = pdfAsset;
    previousClipId.current = clipId;

    if (!pdfAsset) {
      return;
    }

    // When navigating to a new clip, we want to abort the previous fetch
    abortController.current?.abort();
    abortController.current = new AbortController();
    try {
      // We download the PDF asset as an array buffer to be able to pass it to the PDF viewers
      // This is to prevent double triggering of PDF downloads in PDFOutline and PDFVisualizer
      let response = await fetch(pdfAsset, {
        signal: abortController.current.signal,
      });

      if (response.status === 404) {
        const url = await getFallbackUrlRef.current();
        response = await fetch(url, {
          signal: abortController.current.signal,
        });
      }

      if (response.status !== 200) {
        throw new Error('Failed to fetch PDF asset');
      }

      const arrayBuffer = await response.arrayBuffer();
      abortController.current = null;

      setData(arrayBuffer);
    } catch (error) {
      const typedError = convertUnknownToError(error);
      if (typedError.name !== 'AbortError') {
        setHasError(true);
      }
    }
  }, [clipId, pdfAsset]);

  const refetchPdfAsset = useCallback(() => {
    previousClipId.current = undefined;
    downloadingAsset.current = undefined;
    fetchPdfAsset();
  }, [fetchPdfAsset]);

  useEffect(() => {
    fetchPdfAsset();
  }, [fetchPdfAsset]);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
      abortController.current = null;
    };
  }, []);

  return {
    refetchPdfAsset,
    hasError,
    data,
  };
};

export const useSetPageAsThumbnail = () => {
  const boardId = useURLBoardIdSelector();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const clipId = useURLClipIdSelector();
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const clipStatus = useSelector(centralizedClipStatusSelector);
  const hasBegunProcessing = useRef<boolean>(false);
  const { updateAssetInAllViews } = useUpdateAssetInAllViews();
  const { showToast } = useToasts();
  const queryClient = useQueryClient();
  const { trackSetCustomPdfThumbnail } = useTrackSetCustomPdfThumbnail();

  const timer = useRef<number>(0);

  useEffect(() => {
    updateAssetInAllViews({ asset: { id: clipId, status: clipStatus as ClipStatus } });
    if (clipStatus === ClipStatus.uploaded || clipStatus === ClipStatus.transcoding) {
      hasBegunProcessing.current = true;
    }

    if (hasBegunProcessing.current && clipStatus === ClipStatus.transcoded) {
      clearInterval(timer.current);
      updateAssetInAllViews({ asset: { id: clipId } });
      queryClient.invalidateQueries({ queryKey: getVersionsListKey({ assetId }) });
    }
  }, [clipStatus, assetId, clipId, updateAssetInAllViews, queryClient]);

  useUnmount(() => {
    clearInterval(timer.current);
  });

  const setPageAsThumbnail = useCallback(
    async ({
      frameNumber,
      analyticsLocation,
    }: {
      frameNumber: number;
      analyticsLocation: AirActionTrackingLocation;
    }) => {
      if (!clipId || !workspaceId) {
        return;
      }

      await Assets.reprocessAssetVersion({ assetId, clipId, frameNumber, workspaceId });

      queryClient.setQueryData<Clip | null>(getClipStatusUpdaterKey(clipId), (data) =>
        data ? { ...data, status: ClipStatus.uploaded } : null,
      );

      updateAssetInAllViews({ asset: { id: clipId, status: ClipStatus.uploaded } });
      timer.current = window.setInterval(() => {
        queryClient.invalidateQueries({ queryKey: getVersionsListKey({ assetId }) });
      }, 2000);

      trackSetCustomPdfThumbnail({ clipId, boardId: boardId || '', location: analyticsLocation });

      showToast('Thumbnail processing. This could take a minute.', {
        timeBeforeAutoDismiss: undefined,
        type: 'assertive',
        withCloseButton: true,
      });
    },

    [clipId, assetId, workspaceId, queryClient, updateAssetInAllViews, trackSetCustomPdfThumbnail, boardId, showToast],
  );

  return { setPageAsThumbnail };
};

export const useGetPageMenuOptions = () => {
  const { setPageAsThumbnail } = useSetPageAsThumbnail();
  const { copyTextToClipboard } = useCopyTextToClipboard();

  const getPageMenuOptions = useCallback(
    ({
      analyticsLocation,
      pageNumber,
    }: {
      analyticsLocation: AirActionTrackingLocation;
      pageNumber?: number;
    }): DropdownMenuOption[] => {
      return [
        {
          id: `page-${pageNumber}-set-as-thumbnail`,
          label: 'Set as thumbnail',
          onSelect: () => (pageNumber ? setPageAsThumbnail({ frameNumber: pageNumber - 1, analyticsLocation }) : noop),
          type: 'item',
        },
        {
          id: `page-${pageNumber}-copy-link`,
          label: 'Copy link to this page',
          onSelect: () => {
            const newUrl = `${window.location.origin}${window.location.pathname}?${QueryParamNames.page}=${pageNumber}`;

            copyTextToClipboard({
              errorMessage: 'Failed to copy link',
              successMessage: 'Link copied to clipboard',
              text: newUrl,
            });
          },
          type: 'item',
        },
      ];
    },
    [copyTextToClipboard, setPageAsThumbnail],
  );

  return { getPageMenuOptions };
};
